import * as THREE from 'three'
import type {
  GSplatGraphicsAdapter,
  GSplatTextureDescriptor,
  GSplatTextureLeaf,
} from '@/plugins/gsplat/graphics/GSplatGraphicsAdapter'
import type { GSplatStreamDescriptor } from '@/plugins/gsplat/lod/GSplatFormat'
import type { GSplatWorkBufferModifier, GSplatWorkBufferUpdateMode } from '@/shared/types/gsplat'
import type { GSplatTrees } from './GSplatTrees'
import { type GSplatTreesPlan, GSplatTreesPlanError } from './GSplatTreesPlan'

const TREE_TEXTURE_USAGES = Object.freeze(['sampled', 'copy-src', 'copy-dst'] as const)

export interface GSplatTreesParameterTarget {
  setParameter(name: string, value: unknown): void
  getParameter?(name: string): unknown
  deleteParameter?(name: string): void
}

export interface GSplatTreesPlacementTarget extends GSplatTreesParameterTarget {
  setWorkBufferModifier(modifier: GSplatWorkBufferModifier | null): unknown
  setWorkBufferUpdateMode(mode: GSplatWorkBufferUpdateMode): unknown
}

export interface GSplatTreesGpuResources {
  readonly sphereTexture: GSplatTextureLeaf
  readonly gridTexture: GSplatTextureLeaf
  readonly listTexture: GSplatTextureLeaf
  readonly parameters: ReadonlyMap<string, unknown>
  apply(target: GSplatTreesParameterTarget): void
  detach(target: GSplatTreesParameterTarget): void
  dispose(): void
}

export interface GSplatTreesAttachment {
  readonly resources: GSplatTreesGpuResources
  rebuild(): void
  dispose(): void
}

/**
 * Adapter-owned copy-pass inputs for Trees.
 *
 * The textures intentionally use one texel per logical element. The copy shaders
 * use `texelFetch/textureLoad` with explicit integer coordinates, so the generic
 * square packing helper must not be used here.
 */
export function createGSplatTreesGpuResources(
  adapter: GSplatGraphicsAdapter,
  plan: GSplatTreesPlan,
  label = 'gsplat-trees'
): GSplatTreesGpuResources {
  const descriptors = createDescriptors(plan, label)
  for (const descriptor of descriptors) validateDescriptor(adapter, descriptor)

  const created: GSplatTextureLeaf[] = []
  try {
    for (const descriptor of descriptors) created.push(adapter.createTexture(descriptor))
  } catch (error) {
    for (const texture of created) texture.dispose()
    throw error
  }

  const [sphereTexture, gridTexture, listTexture] = created
  if (!sphereTexture || !gridTexture || !listTexture) {
    for (const texture of created) texture.dispose()
    throw new Error('GSplatTrees GPU resource 创建未返回完整 texture 集合')
  }

  let disposed = false
  const parameters = new Map<string, unknown>([
    ['uSphereTex', sphereTexture],
    ['uGridTex', gridTexture],
    ['uListTex', listTexture],
    ['uGridMin', new THREE.Vector2(plan.gridMin[0], plan.gridMin[1])],
    ['uGridInvCell', new THREE.Vector2(plan.gridInvCell[0], plan.gridInvCell[1])],
    ['uGridDims', new THREE.Vector2(plan.gridDims[0], plan.gridDims[1])],
  ])

  return {
    sphereTexture,
    gridTexture,
    listTexture,
    parameters: Object.freeze(parameters),
    apply(target) {
      if (disposed) throw new Error('GSplatTrees GPU resources 已释放')
      for (const [name, value] of parameters) target.setParameter(name, value)
    },
    detach(target) {
      if (disposed || !target.deleteParameter) return
      for (const [name, value] of parameters) {
        if (!target.getParameter || target.getParameter(name) === value) {
          target.deleteParameter(name)
        }
      }
    },
    dispose() {
      if (disposed) return
      disposed = true
      for (const texture of created) texture.dispose()
    },
  }
}

/**
 * Attach copy resources, the WorkBuffer modifier and its one-shot update policy
 * to one placement target. Rebuilds stage a fresh texture set before replacing
 * the target parameters.
 */
export function attachGSplatTrees(input: {
  readonly trees: GSplatTrees
  readonly adapter: GSplatGraphicsAdapter
  readonly target: GSplatTreesPlacementTarget
  readonly publishFormat: (streams: readonly GSplatStreamDescriptor[]) => boolean
  readonly label?: string
}): GSplatTreesAttachment {
  let resources = input.trees.createGpuResources(input.adapter, input.label)
  let disposed = false

  try {
    if (!input.trees.ensureFormat(input.publishFormat)) {
      throw new Error('GSplatTrees treeBind format 发布未提交')
    }
    resources.apply(input.target)
    requireTargetSuccess(input.target.setWorkBufferModifier(input.trees.workBufferModifier))
    requireTargetSuccess(input.target.setWorkBufferUpdateMode('once'))
  } catch (error) {
    resources.detach(input.target)
    resources.dispose()
    throw error
  }

  return {
    get resources() {
      return resources
    },
    rebuild() {
      if (disposed) throw new Error('GSplatTrees attachment 已释放')
      const next = input.trees.createGpuResources(input.adapter, input.label)
      const previous = resources
      const backup = captureParameters(input.target, previous.parameters)
      try {
        next.apply(input.target)
      } catch (error) {
        restoreParameters(input.target, backup, next.parameters)
        next.dispose()
        throw error
      }
      previous.detach(input.target)
      previous.dispose()
      resources = next
    },
    dispose() {
      if (disposed) return
      disposed = true
      try {
        resources.detach(input.target)
        input.target.setWorkBufferModifier(null)
        input.target.setWorkBufferUpdateMode('auto')
      } finally {
        resources.dispose()
      }
    },
  }
}

function createDescriptors(
  plan: GSplatTreesPlan,
  label: string
): readonly GSplatTextureDescriptor[] {
  const sphereWidth = requireTextureWidth(plan.sphereData, 'sphere')
  const listWidth = requireTextureWidth(plan.listData, 'list')
  const gridWidth = requireDimension(plan.gridDims[0], 'grid width')
  const gridHeight = requireDimension(plan.gridDims[1], 'grid height')

  return Object.freeze([
    createDescriptor(`${label}:sphere`, sphereWidth, 1, plan.sphereData),
    createDescriptor(`${label}:grid`, gridWidth, gridHeight, plan.gridData),
    createDescriptor(`${label}:list`, listWidth, 1, plan.listData),
  ])
}

function createDescriptor(
  label: string,
  width: number,
  height: number,
  data: Float32Array
): GSplatTextureDescriptor {
  return {
    label,
    width,
    height,
    format: 'rgba32float',
    usages: TREE_TEXTURE_USAGES,
    data,
    minFilter: 'nearest',
    magFilter: 'nearest',
    addressU: 'clamp-to-edge',
    addressV: 'clamp-to-edge',
  }
}

function validateDescriptor(
  adapter: GSplatGraphicsAdapter,
  descriptor: GSplatTextureDescriptor
): void {
  const dimensionLimit = adapter.limits.maxTextureDimension2D
  if (descriptor.width > dimensionLimit || descriptor.height > dimensionLimit) {
    throw new GSplatTreesPlanError(
      `Trees ${descriptor.label} 超过纹理尺寸限制: actual=${descriptor.width}x${descriptor.height}, ` +
        `limit=${dimensionLimit}`,
      {
        name: descriptor.label,
        actual: Math.max(descriptor.width, descriptor.height),
        limit: dimensionLimit,
      }
    )
  }

  const info = adapter.getTextureFormatInfo(descriptor.format)
  const byteLength = descriptor.width * descriptor.height * info.bytesPerTexel
  if (byteLength > adapter.limits.maxBufferSize) {
    throw new GSplatTreesPlanError(
      `Trees ${descriptor.label} 超过资源字节限制: actual=${byteLength}, limit=${adapter.limits.maxBufferSize}`,
      {
        name: `${descriptor.label}:bytes`,
        actual: byteLength,
        limit: adapter.limits.maxBufferSize,
      }
    )
  }

  try {
    adapter.validateTextureDescriptor(descriptor)
  } catch (error) {
    throw new GSplatTreesPlanError(
      `Trees ${descriptor.label} 纹理预检失败: ${
        error instanceof Error ? error.message : String(error)
      }`
    )
  }
}

function requireTextureWidth(data: Float32Array, name: string): number {
  if (data.length === 0 || data.length % 4 !== 0) {
    throw new GSplatTreesPlanError(`Trees ${name} payload 必须是 RGBA32F 对齐数组`)
  }
  return Math.max(1, data.length / 4)
}

function requireDimension(value: number, name: string): number {
  if (!Number.isSafeInteger(value) || value < 1) {
    throw new GSplatTreesPlanError(`Trees ${name} 非法: ${value}`)
  }
  return value
}

function requireTargetSuccess(result: unknown): void {
  if (result === false) throw new Error('GSplatTrees placement target 拒绝了绑定变更')
}

function captureParameters(
  target: GSplatTreesParameterTarget,
  parameters: ReadonlyMap<string, unknown>
): ReadonlyMap<string, unknown> {
  if (!target.getParameter) return new Map()
  return new Map([...parameters].map(([name]) => [name, target.getParameter?.(name)]))
}

function restoreParameters(
  target: GSplatTreesParameterTarget,
  previous: ReadonlyMap<string, unknown>,
  next: ReadonlyMap<string, unknown>
): void {
  if (!target.getParameter) return
  for (const [name, value] of previous) target.setParameter(name, value)
  if (!target.deleteParameter) return
  for (const name of next.keys()) {
    if (!previous.has(name) && target.getParameter(name) !== undefined) {
      target.deleteParameter(name)
    }
  }
}
